/**
 * Scope Guard for the Worker PR completion loop. Pure and deterministic.
 *
 * Before the loop merges a Worker pull request it compares the diff at the exact head it is
 * about to merge with the task's instructions and the owner's corrections for that task. A
 * new table, view, callable function (a `public` RPC or a `brunel_engine` function), screen,
 * setting or workflow that the task did not ask for is extra scope: the merge does not
 * happen, the Worker is told exactly what to strip, and each extra goes to the owner as a
 * proposal. Clearance belongs to the inspected head only; any other head is compared afresh.
 *
 * This module decides; it reads nothing and writes nothing. Missing evidence is never
 * guessed into a clearance: the verdict is `unavailable` and the merge stays pending.
 */

export const SCOPE_ADDITION_KINDS = ['table', 'view', 'function', 'screen', 'setting', 'workflow'] as const;
export type ScopeAdditionKind = (typeof SCOPE_ADDITION_KINDS)[number];

/** One changed file of a pull request, as the files list reports it. Null patch: not readable. */
export interface ScopeDiffFile {
  readonly path: string;
  readonly status: string;
  readonly patch: string | null;
}

/** The diff read at one exact head. */
export interface ScopeDiff {
  readonly headSha: string;
  readonly files: readonly ScopeDiffFile[];
}

/** What the task asked for: its approved instructions and the owner's corrections to it. */
export interface ScopeRequest {
  readonly title: string;
  readonly instructions: string;
  readonly corrections: readonly string[];
}

export interface ScopeAddition {
  readonly kind: ScopeAdditionKind;
  /** Schema-qualified for database objects; the file name for files. */
  readonly name: string;
  readonly path: string;
  /** The added line (database objects) or the added file, as evidence. */
  readonly evidence: string;
}

export type ScopeVerdict =
  | { readonly kind: 'clear'; readonly headSha: string }
  | { readonly kind: 'extra'; readonly headSha: string; readonly extras: readonly ScopeAddition[] }
  | { readonly kind: 'unavailable'; readonly reason: string };

/** At most this many extras are named per verdict, so a repair brief stays bounded. */
export const SCOPE_MAX_EXTRAS = 10;

const SHA = /^[0-9a-f]{40}$/;
const NAME = '((?:"?[A-Za-z_][A-Za-z0-9_]*"?\\.)?"?[A-Za-z_][A-Za-z0-9_]*"?)';
const SQL_TABLE = new RegExp(`\\bcreate\\s+(?:unlogged\\s+)?table\\s+(?:if\\s+not\\s+exists\\s+)?${NAME}`, 'i');
const SQL_VIEW = new RegExp(`\\bcreate\\s+(?:or\\s+replace\\s+)?(?:materialized\\s+)?view\\s+(?:if\\s+not\\s+exists\\s+)?${NAME}`, 'i');
const SQL_FUNCTION = new RegExp(`\\bcreate\\s+(?:or\\s+replace\\s+)?(?:function|procedure)\\s+${NAME}\\s*\\(`, 'i');
/** Only the callable surface counts as a new RPC/function; `brunel_private` helpers are internals. */
const CALLABLE_SCHEMAS = new Set(['public', 'brunel_engine']);

const SQL_FILE = /^supabase\/(?:migrations|functions)\/.+\.sql$/;
const WORKFLOW_FILE = /^\.github\/workflows\/[^/]+\.ya?ml$/;
const TEST_FILE = /(?:^|\/)(?:tests?|__tests__|fixtures)\/|\.(?:test|spec)\.[a-z]+$/;
const SETTING_FILE = /^apps\/[^/]+\/(?:src\/.+Settings?\.tsx|App\/.+Settings?View\.swift)$/;
const SCREEN_FILE = /^apps\/[^/]+\/(?:src\/components\/.+(?:Panel|View|Page|Screen|Room|Board|Workspace|Center|Dialog)\.tsx|App\/.+View\.swift)$/;

function bareName(raw: string): string {
  const parts = raw.replaceAll('"', '').toLowerCase().split('.');
  return parts.length === 1 ? `public.${parts[0]}` : `${parts[0]}.${parts[1]}`;
}

function fileName(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

/** Lines the pull request adds, without the diff marker. */
function addedLines(patch: string): string[] {
  return patch.split('\n').filter((line) => line.startsWith('+') && !line.startsWith('+++')).map((line) => line.slice(1));
}

/**
 * Every table, view, callable function, screen, setting and workflow the diff adds, before
 * asking whether the task requested it. Database objects that already exist are removed by
 * the caller (`withoutExisting`), since a migration redefining one adds nothing new.
 */
export function scopeAdditions(diff: ScopeDiff): { readonly ok: true; readonly additions: readonly ScopeAddition[] } | { readonly ok: false; readonly reason: string } {
  const additions: ScopeAddition[] = [];
  const seen = new Set<string>();
  const add = (addition: ScopeAddition) => {
    const key = `${addition.kind}:${addition.name}`;
    if (seen.has(key)) return;
    seen.add(key);
    additions.push(addition);
  };
  for (const file of diff.files) {
    if (file.status === 'removed') continue;
    const added = file.status === 'added';
    if (SQL_FILE.test(file.path)) {
      if (file.patch === null) return { ok: false, reason: `diff_unreadable:${file.path}` };
      for (const line of addedLines(file.patch)) {
        const code = line.replace(/--.*$/, '');
        if (code.trim() === '') continue;
        const evidence = line.trim().slice(0, 160);
        const table = SQL_TABLE.exec(code);
        if (table) add({ kind: 'table', name: bareName(table[1]!), path: file.path, evidence });
        const view = SQL_VIEW.exec(code);
        if (view) add({ kind: 'view', name: bareName(view[1]!), path: file.path, evidence });
        const fn = SQL_FUNCTION.exec(code);
        if (fn) {
          const name = bareName(fn[1]!);
          if (CALLABLE_SCHEMAS.has(name.split('.')[0]!)) add({ kind: 'function', name, path: file.path, evidence });
        }
      }
      continue;
    }
    if (!added || TEST_FILE.test(file.path)) continue;
    if (WORKFLOW_FILE.test(file.path)) add({ kind: 'workflow', name: fileName(file.path), path: file.path, evidence: `new file ${file.path}` });
    else if (SETTING_FILE.test(file.path)) add({ kind: 'setting', name: fileName(file.path), path: file.path, evidence: `new file ${file.path}` });
    else if (SCREEN_FILE.test(file.path)) add({ kind: 'screen', name: fileName(file.path), path: file.path, evidence: `new file ${file.path}` });
  }
  return { ok: true, additions };
}

/** The database objects among the additions, for the existence check against the live schema. */
export function scopeDatabaseObjects(additions: readonly ScopeAddition[]): { readonly kind: 'table' | 'view' | 'function'; readonly name: string }[] {
  return additions.flatMap((a) => (a.kind === 'table' || a.kind === 'view' || a.kind === 'function' ? [{ kind: a.kind, name: a.name }] : []));
}

function words(value: string): string[] {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((w) => w !== '')
    .map((w) => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w));
}

const KIND_WORDS: Readonly<Record<ScopeAdditionKind, readonly string[]>> = {
  table: ['table'],
  view: ['view'],
  function: ['function', 'rpc', 'procedure'],
  screen: ['screen', 'page', 'panel', 'view', 'tab', 'dialog'],
  setting: ['setting', 'toggle', 'preference'],
  workflow: ['workflow'],
};
const ASKING_VERBS = new Set(['add', 'adding', 'create', 'creating', 'introduce', 'introducing', 'build', 'building', 'new']);
const NOT_ASKING = new Set(['no', 'not', 'without', 'never', 'dont', 'nor', 'if', 'unless', 'whether', 'avoid']);
const FILE_SUFFIX = /\.(?:tsx|swift|ya?ml)$/;

/** "add a new settings toggle" asks for a setting; "add no new table" and "if it adds a table" do not. */
function asksForKind(text: readonly string[], kind: ScopeAdditionKind): boolean {
  const wanted = KIND_WORDS[kind];
  for (let i = 0; i < text.length; i += 1) {
    if (!ASKING_VERBS.has(text[i]!)) continue;
    if (NOT_ASKING.has(text[i - 1] ?? '') || NOT_ASKING.has(text[i - 2] ?? '')) continue;
    for (let j = i + 1; j <= i + 4 && j < text.length; j += 1) {
      if (NOT_ASKING.has(text[j]!)) break;
      if (wanted.includes(text[j]!)) return true;
    }
  }
  return false;
}

function contains(haystack: readonly string[], needle: readonly string[]): boolean {
  if (needle.length === 0) return false;
  outer: for (let i = 0; i + needle.length <= haystack.length; i += 1) {
    for (let j = 0; j < needle.length; j += 1) if (haystack[i + j] !== needle[j]) continue outer;
    return true;
  }
  return false;
}

/** Did the task (instructions or owner corrections) ask for this addition, by name or by kind? */
export function scopeRequested(addition: ScopeAddition, request: ScopeRequest): boolean {
  const raw = [request.title, request.instructions, ...request.corrections].join('\n');
  const text = words(raw);
  const object = addition.kind === 'table' || addition.kind === 'view' || addition.kind === 'function'
    ? addition.name.slice(addition.name.indexOf('.') + 1)
    : addition.name.replace(FILE_SUFFIX, '');
  const named = words(object);
  // A one-word object ("notes") is named only in full ("brunel.notes", "NotesPanel.tsx"), not
  // by the ordinary word; a longer one by its identifier or its words ("merge route settings").
  const identifiers = named.length >= 2 ? [object, addition.name] : [addition.name];
  for (const identifier of identifiers) {
    const escaped = identifier.toLowerCase().replace(/[^a-z0-9_.-]/g, '').replaceAll('.', '\\.');
    if (escaped !== '' && new RegExp(`(?:^|[^a-z0-9_])${escaped}(?:$|[^a-z0-9_])`).test(raw.toLowerCase())) return true;
  }
  if (named.length >= 2 && contains(text, named)) return true;
  return asksForKind(text, addition.kind);
}

/**
 * The verdict for one exact head. `existing` names the database objects the live schema
 * already has (a redefinition adds nothing). Clearance is issued only for `expectedHeadSha`.
 */
export function evaluateScope(input: {
  readonly expectedHeadSha: string;
  readonly diff: ScopeDiff | null;
  readonly diffUnavailable?: string;
  readonly request: ScopeRequest | null;
  readonly requestUnavailable?: string;
  readonly existing?: readonly string[];
}): ScopeVerdict {
  if (!SHA.test(input.expectedHeadSha)) return { kind: 'unavailable', reason: 'head_sha_unknown' };
  if (input.diff === null) return { kind: 'unavailable', reason: `diff_unavailable:${input.diffUnavailable ?? 'unknown'}` };
  if (input.diff.headSha !== input.expectedHeadSha) return { kind: 'unavailable', reason: 'head_moved' };
  if (input.request === null) return { kind: 'unavailable', reason: `instructions_unavailable:${input.requestUnavailable ?? 'unknown'}` };
  if (input.request.instructions.trim() === '' && input.request.title.trim() === '') return { kind: 'unavailable', reason: 'instructions_unavailable:empty' };
  const found = scopeAdditions(input.diff);
  if (!found.ok) return { kind: 'unavailable', reason: found.reason };
  const existing = new Set((input.existing ?? []).map((name) => name.toLowerCase()));
  const request = input.request;
  const extras = found.additions
    .filter((a) => !((a.kind === 'table' || a.kind === 'view' || a.kind === 'function') && existing.has(a.name)))
    .filter((a) => !scopeRequested(a, request))
    .slice(0, SCOPE_MAX_EXTRAS);
  return extras.length === 0
    ? { kind: 'clear', headSha: input.expectedHeadSha }
    : { kind: 'extra', headSha: input.expectedHeadSha, extras };
}
